import React, { Component, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
// import EditOrder from './editOrderDetail';
import { connect } from 'react-redux';
import { getOrderDetails } from 'actions/admin'
import moment from 'moment';

/*
Campaign rewards come READY from getOrderDetails, one block per tier, and only for a tier that
awarded something:

    orderDetails.seller_level / .platform_level : { type: 'cashback', cashback_allocated }
                                                  { type: 'gift', description, price, brand }
    line.productlevel                           : { type: 'coupon', discount_given }
                                                  { type: 'gift', description, price, brand }

`discount_given` is the amount actually granted on that line, reconciled by the API against the
order's `coupon_discount`. This screen used to compute it here from coupon_granted / coupon_value /
coupon_min_qty, in a copy kept in step by hand with the seller-side one in medimny-web; those raw
fields are no longer sent, and the reconciliation lives in the API (controllers/order.js
lineCouponAmounts) where there is one of it.
*/
const cashbackOf = (block) => (block && block.type === 'cashback' ? Number(block.cashback_allocated) || 0 : 0);
const couponOf = (block) => (block && block.type === 'coupon' ? Number(block.discount_given) || 0 : 0);
const giftOf = (block) => (block && block.type === 'gift' ? block.description : '');

/*
TOTAL QTY - what the seller ships, as against `quantity`, which is what the buyer paid for.

Derived here rather than stored: the order line already carries everything this needs - its
`quantity` plus the scheme snapshotted onto it at placement - so the API sends nothing extra for
this column. Only a same-product scheme ('Same' / 'SameAndDiscount') adds units, at
(quantity / purchase) x bonus; every other line ships exactly its quantity.

Unfloored, deliberately: it is the figure the line was PRICED on (see getFinalValue below), so a
part-lot quantity shows the part bonus it actually paid for.
*/
const SAME_PRODUCT_SCHEMES = ['Same', 'SameAndDiscount'];

const totalQuantityOf = (data) => {
    if (!data) return 0;
    const qty = Number(data.quantity) || 0;
    const dop = data.discount_on_product;
    const purchase = Number(dop && dop.purchase) || 0;
    const bonus = Number(dop && dop.bonus) || 0;
    if (!SAME_PRODUCT_SCHEMES.includes(data.discount_name) || purchase <= 0 || bonus <= 0) return qty;
    return qty + ((qty / purchase) * bonus);
};

// Whole units print bare; a part-lot bonus keeps its decimals rather than being rounded into a
// quantity the order was not priced on.
const formatQty = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(2));

/*
One padding value for every cell, header and body alike, so the columns stay evenly spaced as they
are added to. Only Title and Company Name wrap - they are the two free-text columns, and letting the
short numeric ones wrap is what makes a table this wide look cluttered.
*/
const cellStyle = { padding: '12px 16px', whiteSpace: 'nowrap', verticalAlign: 'top' };
const textCellStyle = { ...cellStyle, whiteSpace: 'normal', minWidth: 170 };
/*
Title carries by far the longest text on the row - a product name, sometimes with a surcharge or
campaign badge under it - so it gets the widest floor of any column. The table already scrolls
sideways inside its Paper, so the extra width costs a little more scrolling on a narrow screen
rather than squeezing the other columns.

Company Name is the column that gives way. It is capped rather than floored, and `breakWord` lets a
single long marketing-company name break mid-word instead of pushing the column past that cap. A
company name over two lines is the trade being made for a title on one.
*/
const titleCellStyle = { ...textCellStyle, minWidth: 280 };
const companyCellStyle = { ...textCellStyle, minWidth: 140, maxWidth: 200, wordBreak: 'break-word' };

function OrderDetailTable(props) {

    const [state, setState] = useState({
        orderProductData: [],
        status: ''
    })

    const handleClick = (e, value, ptr, quantity, index) => {
        if (quantity !== '' && ptr !== '') {
            let tempOrderProduct = state.orderProductData;
            tempOrderProduct[index].paid = ptr;
            tempOrderProduct[index].quantity = quantity;
            setState((prev)=>({...prev, orderProductData: tempOrderProduct }));
            props.onClickHandle(e, value, tempOrderProduct);
        } else {
            props.onClickHandle(e, value, '');
        }
    }

    useEffect(() => {
        setState((prev)=>({...prev, orderProductData: props.orderDetails.products, status: props.orderDetails.requested }));
    }, [])

    useEffect(() => {
        setState((prev)=>({...prev, orderProductData: props.orderDetails?.products, status: props.orderDetails?.requested }));
    }, [props.orderDetails])

    const button = (title, qty, index, cost) => {
        if (title !== undefined, qty !== undefined, index !== undefined, cost !== undefined) {
            return (
                < div className={'action'} >
                </div >
            )
        }
    }
    const getValue = (data) => {
        if (!data.discount_on_product && data.discount_per > 0) {
            let totalPtr = (data.PTR - ((data.PTR / 100) * data.discount_per))
            return totalPtr * Number(data.quantity)
        } else if (data.discount_on_product && data.discount_per > 0) {
            let totalPtr = data.inventory_id.ePTR
            return totalPtr * Number(data.quantity)
        } else {
            return data.PTR * Number(data.quantity)
        }
    }

    const getSingleItemFinalValue = (data) => {
        if (!data.discount_on_product && data.discount_per > 0) {
            let totalPtrSingle = (data.PTR - ((data.PTR / 100) * data.discount_per))
            return totalPtrSingle
        } else if (data.discount_on_product && data.discount_per > 0) {
            let totalPtr = data.inventory_id.ePTR
            return totalPtr
        } else {
            return data.PTR
        }
    }

    const getFinalValue = (data) => {
        let tempValue = data.discount_name && (data.discount_name == 'Same' || data.discount_name == 'SameAndDiscount') ?
            (((data.quantity / data.discount_on_product.purchase) * data.discount_on_product.bonus) + Number(data.quantity)) * (data.ePTR ? data.ePTR : data.PTR)
            : (data.ePTR ? data.ePTR : data.PTR) * data.quantity
        let finalValue = ((Number(data.GST) / 100) * tempValue) + tempValue;
        return finalValue
    }

    let { orderProductData, status } = state;
    // Seller-level and platform-level rewards attach to the ORDER, not to any single line, so they
    // are shown above the product table.
    const od = props.orderDetails || {};
    const orderCashback = cashbackOf(od.seller_level);
    const orderPlatform = cashbackOf(od.platform_level);
    const orderSellerGift = giftOf(od.seller_level);
    const orderPlatformGift = giftOf(od.platform_level);
    const hasOrderOffers = orderCashback > 0 || orderPlatform > 0 || orderSellerGift || orderPlatformGift;
    return (
        <React.Fragment>
            {hasOrderOffers &&
                <Paper className={'tableRoot'} style={{ padding: '12px 16px', marginBottom: '12px' }}>
                    <h5 style={{ color: '#072791', marginBottom: '8px' }}>Order-level offers</h5>
                    {orderCashback > 0 && <div className='text-success'>Seller Cashback: &#x20B9;{orderCashback.toFixed(2)}</div>}
                    {orderSellerGift && <div style={{ color: '#ef6c00' }}>Seller Gift: {orderSellerGift}</div>}
                    {orderPlatform > 0 && <div className='text-success'>Medimny Cashback: &#x20B9;{orderPlatform.toFixed(2)}</div>}
                    {orderPlatformGift && <div style={{ color: '#ef6c00' }}>Medimny Gift: {orderPlatformGift}</div>}
                </Paper>
            }
            {/* The two added columns take the table past the width of a laptop screen, so it scrolls
                sideways inside the Paper rather than squeezing every column thinner. */}
            <Paper className={'tableRoot'} style={{ overflowX: 'auto' }}>
            {orderProductData && orderProductData.length > 0 ?
                <Table className={'tableMain'} aria-label="spanning table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" style={titleCellStyle}>Title</TableCell>
                            <TableCell align="left" style={companyCellStyle}>Company Name</TableCell>
                            <TableCell align="left" style={cellStyle}>Packing</TableCell>
                            <TableCell align="left" style={cellStyle}>Expiry</TableCell>
                            <TableCell align="left" style={cellStyle}>Offer</TableCell>
                            <TableCell align="left" style={cellStyle}>MRP</TableCell>
                            <TableCell align="left" style={cellStyle}>PTR</TableCell>

                            <TableCell align="left" style={cellStyle}>Qty</TableCell>
                            <TableCell align="left" style={cellStyle}>Total Qty</TableCell>
                            <TableCell align="left" style={cellStyle}>Effective PTR</TableCell>
                            <TableCell align="left" style={cellStyle}>GST</TableCell>
                            <TableCell align="left" style={cellStyle}>Final Price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderProductData.map((eachProduct, index) => (
                            <TableRow key={eachProduct.title + '' + index}>

                                <TableCell align="left" style={titleCellStyle}>
                                    {eachProduct.inventory_id.medicineTypeName === 'Others' ?
                                        <Tooltip title={`PCD Product`}>
                                            <span className="size-8 rounded-circle d-inline-block" style={{ backgroundColor: 'orange', marginLeft: "-10px", marginRight: "3px" }} />
                                        </Tooltip> : ''}

                                    {eachProduct.name} <br />
                                    {eachProduct.surCharge && eachProduct.surCharge > 0 ? <span className='text-white bg-danger' style={{ padding: "1px 5px" }}>Surcharge ({eachProduct.surCharge}%)</span> : ''}
                                    {/* One product-level campaign per line, so the block is a coupon
                                        or a gift and never both. */}
                                    {couponOf(eachProduct.productlevel) > 0 ? <div style={{ marginTop: '2px' }}>
                                        <span className='text-white' style={{ backgroundColor: '#0a8f2c', padding: "1px 5px", display: 'inline-block' }}>Product Coupon: &#x20B9;{couponOf(eachProduct.productlevel).toFixed(2)} off</span>
                                    </div> : ''}
                                    {giftOf(eachProduct.productlevel) ? <div style={{ marginTop: '2px' }}>
                                        <span className='text-white bg-magenta' style={{ padding: "1px 5px", display: 'inline-block' }}>Product Gift: {giftOf(eachProduct.productlevel)}</span>
                                    </div> : ''}
                                </TableCell>
                                {/* Marketing company off the product bank, populated by
                                    getOrderDetails. Blank on a line whose product has none. */}
                                <TableCell align="left" style={companyCellStyle}>
                                    {eachProduct.inventory_id?.product_id?.company_id?.name || '-'}
                                </TableCell>
                                <TableCell align="left" style={cellStyle}>{eachProduct.inventory_id.product_id.Type.name}</TableCell>
                                <TableCell align="left" style={cellStyle}>{moment(eachProduct && eachProduct.expiry_date).format('MM/YYYY')}</TableCell>
                                <TableCell align="left" style={cellStyle}>
                                    {
                                        eachProduct.discount_name && eachProduct.discount_name == 'SameAndDiscount' ?
                                            eachProduct.discount_on_product.purchase + '+' + eachProduct.discount_on_product.bonus + ' (Bonus) and ' + eachProduct.discount_per + '% Off' :

                                            eachProduct.discount_name && eachProduct.discount_name == 'DifferentAndDiscount' ?
                                                eachProduct.discount_on_product.purchase + '+' + eachProduct.discount_on_product.bonus + ' ' + eachProduct.discount_on_product.name + ' and ' + eachProduct.discount_per + '% Off'
                                                :

                                                eachProduct.discount_name && eachProduct.discount_name == 'Same' ?
                                                    eachProduct.discount_on_product.purchase + '+' + eachProduct.discount_on_product.bonus + ' (Bonus)' :

                                                    eachProduct.discount_name && eachProduct.discount_name == 'Different' ?
                                                        eachProduct.discount_on_product.purchase + '+' + eachProduct.discount_on_product.bonus + ' ' + eachProduct.discount_on_product.name
                                                        :
                                                        eachProduct.discount_per + '% discount'
                                    }
                                </TableCell>
                                <TableCell align="left" style={cellStyle}>&#x20B9;{(Number(eachProduct.MRP)).toFixed(2)}</TableCell>
                                <TableCell align="left" style={cellStyle}>&#x20B9;{(Number(eachProduct.PTR)).toFixed(2)}</TableCell>

                                <TableCell align="left" style={cellStyle}>{eachProduct.quantity}</TableCell>
                                {/* Paid quantity + any free units of the SAME product this line's
                                    scheme earned; equal to Qty on every other line. */}
                                <TableCell align="left" style={cellStyle}>{formatQty(totalQuantityOf(eachProduct))}</TableCell>
                                <TableCell align="left" style={cellStyle}>{(eachProduct.ePTR ? eachProduct.ePTR : eachProduct.PTR).toFixed(2)}</TableCell>
                                <TableCell align="left" style={cellStyle}>{(eachProduct.GST? eachProduct.GST: eachProduct.inventory_id.product_id.GST.value) + "%"}</TableCell>
                                <TableCell align="left" style={cellStyle}>{getFinalValue(eachProduct).toFixed(2)}</TableCell>

                                {
                                    status === "New" ?
                                        <TableCell align="left" style={cellStyle}>
                                            {button(eachProduct.name, eachProduct.quantity, index, eachProduct.paid)}
                                        </TableCell>
                                        :
                                        ''
                                }
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                : 'No Items Found'}

            </Paper>
        </React.Fragment>
    );
}

const mapStateToProps = ({ admin }) => {
    const { orderDetails } = admin;
    return { orderDetails }
};

export default connect(mapStateToProps, { getOrderDetails })(OrderDetailTable);
